import { Link, LoaderFunctionArgs, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Autocomplete, Box, Button, Card, Hidden, IconButton, MenuItem, Rating, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Unstable_Grid2';
import Upload from '../common/Upload.tsx';
import Chat from '../common/chat/Chat.tsx';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import HeaderStudio from './HeaderStudio.tsx';
import axios from 'axios';
import { IPackage, IPackageRequest, TYPE_STUDIO } from '../../interfaces/studio.ts';
import handleFormatFile from '../../utils/handleFormatFile.tsx';
import PaymentModal from '../finance/PaymentModal.tsx';
import { UserContext } from '../../contexts/UserContext.tsx';
import { useDebounce } from 'use-debounce';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers.ts';
import ManagementModal from './ManagementModal.tsx';
import { LoadingButton } from '@mui/lab';
import BaranDataGrid from '../common/BaranDataGrid.tsx';
import IResponse from '../../interfaces/response.ts';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import { IStudio } from '../../interfaces/IStudio.ts';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BasicModal from '../common/BasicModal.tsx';
import UploaderFile from '../../utils/UploaderFile.ts';
import DownloadIcon from '@mui/icons-material/Download';

const types = ['create', 'view'];

const Studio = () => {
	const { type, id } = useParams();
	const [searchParams] = useSearchParams();
	const campaignsId_params = searchParams.get('campaign_id');
	const { t } = useTranslation();

	const navigate = useNavigate();
	const headerClassNames = 'text-xs bg-background-paper';
	const [rate, setRate] = useState<number | null>(null);
	const [packageData, setPackageData] = useState<IPackage>();
	const [data, setData] = useState<IPackageRequest>();
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [files, setFiles] = useState<(File | string)[]>([]);
	const [filesDetail, setFilesDetail] = useState<{ id: number; address: string }[]>([]);
	const [ticketId, setTicketId] = useState<number | null | undefined>();
	const [status, setStatus] = useState<TYPE_STUDIO | undefined>(undefined);
	const [feedback, setFeedback] = useState('');
	const [showRejectReason, setShowRejectReason] = useState(true);
	const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
	const [options, setOptions] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [val, setVal] = useState<any | null>(null);
	const [inputValue, setInputValue] = useState('');
	const [search] = useDebounce(inputValue, DELAY_BEFORE_SEARCH);
	const { user } = useContext(UserContext);
	const [open, setOpen] = useState(false);
	const [finishModal, setFinishModal] = useState(false);
	const [fetchedData, setFetchedData] = useState<IStudio[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [deleteFile, setDeleteFile] = useState<IStudio>();
	const [refresh, setRefresh] = useState(1);
	const [studioId, setStudioId] = useState<number | null>(null);
	const [campaingId, setCampaingId] = useState<number | null>(null);

	const handleCloseDeleteDialog = () => {
		setDeleteFile(undefined);
	};
	const handleCloseFinish = () => {
		setFinishModal(false);
	};
	const handleCloseDelete = (file: IStudio) => () => {
		setDeleteFile(file);
	};
	const [label, setLabel] = useState('');
	let total = data?.factor_price ? data?.factor_price : 0;
	useEffect(() => {
		setFilesDetail(p => p.filter(f => files.includes(f?.address ?? '')));
	}, [files]);

	const handleClosePaymentModal = () => {
		setIsPaymentModalOpen(false);
	};

	const handleOpenPaymentModal = () => {
		setIsPaymentModalOpen(true);
	};

	const handleReset = () => {
		total = 0;
	};

	useEffect(() => {
		if (type === 'create') {
			axios({
				method: 'get',
				url: user?.is_admin ? `admin/studio-requests/${id}` : `studio/showPackage/${id}`,
			}).then(r => {
				if (r?.data?.success) {
					setPackageData(r?.data?.data);
					setData(r.data.data);
				}
			});
		} else {
			axios({
				method: 'get',
				url: user?.is_admin ? `admin/studio-requests/${id}` : `studio/showRequest/${id}`,
			}).then(r => {
				if (r?.data?.success) {
					if (user?.is_admin) {
						setPackageData(r?.data?.data);
					} else {
						setPackageData(r?.data?.data?.package);
					}
					setData(r?.data?.data);
					setTitle(r?.data?.data?.title);
					setDescription(r?.data?.data?.description);
					setFiles(r?.data?.data?.images?.map((i: { id: number; address: string }) => i?.address));
					setFilesDetail(r?.data?.data?.images);
					setTicketId(r?.data?.data?.ticket_id);
					setStatus(r?.data?.data?.status);
					setFeedback(r?.data?.data?.feedback);
					setCampaingId(r?.data?.data?.campaign_id);
				}
			});
		}
	}, []);
	const editableData = !status || [TYPE_STUDIO.Rejected, TYPE_STUDIO.PaymentPend, TYPE_STUDIO.Pending].includes(status);

	const changeTitle = (e: ChangeEvent<HTMLInputElement>) => {
		if (user?.is_admin) {
			setTitle(e.target.value);
		} else {
			if (editableData) {
				setTitle(e.target.value);
			}
		}
	};

	const changeDescription = (e: ChangeEvent<HTMLInputElement>) => {
		if (user?.is_admin) {
			setDescription(e.target.value);
		} else {
			if (editableData) {
				setDescription(e.target.value);
			}
		}
	};

	const changeFeedback = (e: ChangeEvent<HTMLInputElement>) => {
		setFeedback(e.target.value);
	};

	const closeRejectReason = () => setShowRejectReason(false);

	const sendRate = () => {
		axios({
			method: 'put',
			url: `studio/update-review/${id}`,
			data: {
				stars: rate,
				feedback: feedback,
			},
		}).then(r => {
			if (r?.data?.success) {
				setData(prev => {
					if (prev) {
						return { ...prev, stars: rate, feedback: feedback };
					} else {
						return prev;
					}
				});
				navigate('/studio/previous-orders');
			}
		});
	};
	useEffect(() => {
		if (user?.is_admin) {
			setLoading(true);
			axios<IResponse<IStudio[]>>({
				url: `admin/studio-requests/files/${id}`,
				method: 'GET',
			})
				.then(res => {
					if (res.data.success) {
						setFetchedData(res.data.data?.map((item, index) => ({ ...item, id: index })));
					}
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [refresh]);

	const handleCloseModal = () => {
		setOpen(false);
	};
	const handleClick = () => {
		setOpen(true);
	};

	const handleSearchUser = (_: any, newValue: any) => {
		setVal(newValue);
	};
	useEffect(() => {
		if (user?.is_admin) {
			(async () => {
				setLoading(true);
				axios({
					url: 'admin/users',
					params: {
						search: JSON.stringify([
							{
								field: 'name',
								value: search,
							},
						]),
					},
				}).then(res => {
					if (res.data.success) {
						setOptions(res.data.data);
					}
				});
				setLoading(false);

				setOptions([...options]);
			})();
		}
	}, [search]);

	const handleDeleteFile = () => {
		setLoading(true);
		axios<IResponse<any>>({
			url: `admin/studio-requests/files/${id}/${deleteFile?.file_id}`,
			method: 'DELETE',
		})
			.then(res => {
				if (res.data.success) {
					setDeleteFile(undefined);
				}
			})
			.finally(() => {
				setLoading(false);
				setRefresh(prev => prev + 1);
			});
	};
	const handleLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
		setLabel(event.target.value);
	};
	const handleFinishRequest = () => {
		setFinishModal(true);
	};
	const handleClickFinished = () => {
		saveData(false);
		setLoading(true);
		axios({
			url: `admin/studio-requests/${id}/update-status`,
			method: 'PUT',
			data: {
				reject_reason: '',
				status: 'completed',
			},
		}).then(res => {
			if (res.data.success) {
				setRefresh(prev => prev + 1);
				setLoading(false);
			}
		});
	};
	const date = new Date().toISOString().split('T')[0];
	const saveData = (is_pement: boolean) => {
		let url = user?.is_admin ? `/admin/studio-requests` : `studio/create-request`;

		const newData = new FormData();

		newData.append('title', title ?? '');
		newData.append('description', description ?? '');
		newData.append('campaign_id', campaignsId_params ?? '');
		files
			?.filter(f => typeof f !== 'string')
			.forEach((file, index) => {
				newData.append(`images[${index}]`, file);
			});
		filesDetail.forEach(({ id }, index) => {
			newData.append(`old_images[${index}]`, id + '');
		});
		newData.append('is_final', (title && description && files.length) || is_pement ? '1' : '0');

		if (val && val.id) {
			newData.append('user_id', val.id);
		}

		if (type !== 'create') {
			url = user?.is_admin ? `admin/studio-requests/${id} ` : `studio/update-request/${id}`;

			newData.append('studio_package_id', (packageData?.id ?? '') + '');
			newData.append('feedback', feedback ? feedback : '');
			newData.append('finished_at', date);
			newData.append('_method', 'put');
		} else {
			if (typeof ticketId === 'number') newData.append('ticket_id', ticketId + '');

			newData.append('studio_package_id', (id ?? '') + '');
		}

		axios.post(url, newData, { loading: true }).then(r => {
			if (r?.data?.success) {
				setStudioId(r?.data?.data);
				user?.is_admin ? navigate('/admin/studio/management') : is_pement ? handleOpenPaymentModal() : navigate('/studio/previous-orders');
			}
		});
	};
	const addFile = () => {
		UploaderFile(files[files.length - 1] as File).then(res => {
			setLoading(true);
			axios({
				url: `/admin/studio-requests/files/${id}`,
				method: 'POST',
				data: {
					file: res.id,
					label: label,
				},
			})
				.then(res => {
					if (res.data.success) {
						setLabel('');
						setFiles([]);
						setOpen(false);
					}
				})
				.finally(() => {
					setLoading(false);
					setRefresh(prev => prev + 1);
				});
		});
	};

	const columns: GridColDef<IStudio>[] = [
		{
			field: 'label',
			headerName: t('title'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'file_url',
			headerName: t('file'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return (
					<Button component={Link} to={params?.value} target="_blank" startIcon={<DownloadIcon />}>
						{t('download')}
					</Button>
				);
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			flex: 1,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: (params: any) => {
				return (
					<>
						<IconButton onClick={handleCloseDelete(params.row)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];
	return (
		<Box className={'mb-6 flex w-full flex-col gap-6'}>
			{status === TYPE_STUDIO.Rejected && showRejectReason && (
				<Alert variant="filled" severity="error" onClose={closeRejectReason} className={'items-center'}>
					{data?.reject_reason}
				</Alert>
			)}

			<HeaderStudio data={packageData}>
				{user?.is_admin && (
					<Button onClick={handleFinishRequest} className={'min-w-40'}>
						{t('finish_request')}
					</Button>
				)}
			</HeaderStudio>

			<Card className={'flex flex-col gap-6 p-6'}>
				{user?.is_admin && type !== 'view' && (
					<Autocomplete
						getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
						filterOptions={x => x}
						options={options}
						autoComplete
						includeInputInList
						filterSelectedOptions
						value={val}
						onChange={handleSearchUser}
						noOptionsText={t('no_user')}
						onInputChange={(_, newInputValue) => {
							setInputValue(newInputValue);
						}}
						renderOption={(props, option) => {
							return (
								<MenuItem {...props} key={option.id} value={val}>
									{option.name ?? option.phone}
								</MenuItem>
							);
						}}
						renderInput={params => <TextField {...params} label={`${t('search')} `} fullWidth></TextField>}
					/>
				)}
				<TextField placeholder={t('title')} onChange={changeTitle} value={title} />
				<Grid container spacing={2}>
					<Grid md={7} xs={12}>
						<TextField placeholder={t('description')} multiline rows={7} className={'w-full'} onChange={changeDescription} value={description} />
					</Grid>
					<Grid md={5} xs={12}>
						<Box className={'flex h-full w-full grow'}>
							{/*todo text uploader*/}
							<Upload text={t('upload_image')} editable={editableData} setFiles={setFiles} files={files} multiple className={'w-full'} />
						</Box>
					</Grid>
				</Grid>
			</Card>
			{status === TYPE_STUDIO.PaymentPend && (
				<Card className={'px-6 py-4'}>
					<Box className="flex items-center justify-between">
						<Box className={'flex items-center gap-4 md:gap-16'}>
							{/*todo font for semibold*/}
							<Typography className={'w-28 text-center font-semibold'}>{t('amount_payable')}</Typography>
							<Typography className={'text-text-secondary'}>
								{data?.factor_price ? data?.factor_price : packageData?.price?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} {'تومان'}
							</Typography>
						</Box>
						<Button variant={'contained'} onClick={() => handleOpenPaymentModal()} className={'h-11 font-medium md:w-72'}>
							{t('payment')}
						</Button>
					</Box>
				</Card>
			)}
			<PaymentModal rout={'/studio/payMultiple'} amount={total} enableType={true} isStudio={true} data={{ studios: [studioId ?? null] }} open={isPaymentModalOpen} onClose={handleClosePaymentModal} handleResultServer={handleReset} link={user?.is_admin ? '/admin/studio/management' : '/studio/previous-orders'} />

			{/*todo : isAdmin*/}
			{typeof ticketId === 'number' && (
				<Card>
					<Chat id={ticketId} layout={false} type={user?.is_admin ? 'admin' : 'user'} />
				</Card>
			)}

			<Card className={'relative mt-8 overflow-visible rounded-s-full px-6 py-4'}>
				<img src={'/images/studio/question.png'} className={'absolute -bottom-1 -right-1'} />
				<Box className={'flex items-center justify-between ps-44'}>
					<Typography className={'text-xl font-semibold'}>{t('do_you_have_any_questions')}</Typography>
					<Button
						variant={'outlined'}
						color={'secondary'}
						className={'h-11 w-72 font-medium'}
						onClick={() => {
							navigate('/support/new-ticket');
						}}
					>
						{t('register_question')}
					</Button>
				</Box>
			</Card>

			{status === TYPE_STUDIO.Completed && (
				<>
					<Card className={'p-6'}>
						{user?.is_admin ? (
							<BaranDataGrid<IStudio> loading={loading} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} onClick2={handleClick} actionButtonLable2={t('add_file')} isCreateBtn2={true} />
						) : (
							<>
								<Typography className="mb-9 text-xl font-medium">{t('final_output')}</Typography>
								<Box className="flex flex-col gap-3">
									{data?.final_result &&
										Object.entries(data.final_result)
											.filter(([_, fileUrl]) => handleFormatFile(fileUrl) === 'image')
											.map(([fileName, fileUrl], index) => (
												<Box key={index} className="flex flex-row-reverse items-center justify-around gap-4">
													<img src={fileUrl} className="h-52 w-full min-w-32 max-w-80 rounded-lg object-cover" />
													<div className={'flex flex-col items-center'}>
														<Button className="w-40" component={Link} to={fileUrl} target="_blank">
															{t('download')}
														</Button>
														<Typography className={'mt-4'}>{fileName}</Typography>
													</div>
												</Box>
											))}
								</Box>
							</>
						)}
					</Card>

					{user?.is_admin ? (
						<Card className={'flex flex-col p-6'}>
							<Box className="mb-6 flex justify-between">
								<Typography>{t('your_score_to_this_request')}</Typography>
								{/*todo ltr*/}
								<Rating value={data?.stars} readOnly />
							</Box>
							<TextField className={'w-full text-sm'} placeholder={t('write_your_opinion')} value={feedback} onChange={changeFeedback} multiline rows={4} />
							<Button onClick={() => saveData(true)} disabled={!feedback} className={'mt-4 w-40 min-w-40 text-left text-sm'}>
								{t('register_opinion')}
							</Button>
						</Card>
					) : data?.stars === null || data?.stars === undefined ? (
						<Card className={'p-6'}>
							<Box className="mb-6 flex justify-between">
								<Typography>{t('register_your_score')}</Typography>
								{/*todo ltr*/}
								<Rating
									value={rate}
									onChange={(_, newValue) => {
										setRate(newValue);
									}}
								/>
							</Box>
							<TextField className={'w-full text-sm'} placeholder={t('write_your_opinion')} value={feedback} defaultValue={''} onChange={changeFeedback} multiline rows={4} />
							<Box className={'mt-4 text-left'}>
								<Button disabled={!(rate && feedback)} onClick={sendRate} className={'text-sm'}>
									{t('register_points')}
								</Button>
							</Box>
						</Card>
					) : (
						<Card className={'p-6'}>
							<Box className="mb-6 flex justify-between">
								<Typography>{t('your_score_to_this_request')}</Typography>
								{/*todo ltr*/}
								<Rating value={data?.stars} readOnly />
							</Box>
							<Typography className={'whitespace-pre rounded border border-grey-400 bg-grey-100 pb-6 pe-9 ps-3 pt-4 text-grey-900'}>{data?.feedback}</Typography>
						</Card>
					)}
				</>
			)}

			{status === TYPE_STUDIO.Completed && (
				<Card className={'relative mt-4 h-28 overflow-visible px-6 py-7'}>
					<Hidden mdDown>
						<img src={'/images/studio/Group.png'} className={'absolute bottom-0 right-2'} />
					</Hidden>
					<Box className={'flex h-full items-end justify-between md:ps-32'}>
						<Box className="flex items-center gap-1">
							<Typography className={'font-bold md:text-2xl'}>{t('yet')}</Typography>
							<Typography className={'bg-gradient-to-l from-primary-main to-secondary-main bg-clip-text text-lg font-bold text-transparent md:text-3xl'}>{t('campaiiign')}</Typography>
							<Typography className={'font-bold md:text-2xl'}>{t('did_not_you_create')}</Typography>
						</Box>

						<Button component={Link} to={`${campaingId ? `/campaigns/view/${campaingId}` : '/campaigns/create'}`} variant={'outlined'} color={'secondary'} className={'h-8 border-0 bg-gradient-to-l from-primary-main to-secondary-main font-medium text-white md:h-12 md:w-80'}>
							{t('create_a_new_campaign')}
						</Button>
					</Box>
				</Card>
			)}
			{user?.is_admin ? (
				<Box className="mb-28 flex justify-end">
					<Button className={'h-12 w-36'} onClick={() => saveData(false)}>
						{t('save')}
					</Button>
				</Box>
			) : (
				((status !== undefined && ![TYPE_STUDIO.ConfirmationPend, TYPE_STUDIO.QueueUp, TYPE_STUDIO.InProgress, TYPE_STUDIO.Completed]?.includes(status)) || type === 'create') && (
					<Box className="mb-28 flex justify-end">
						{type !== 'view' && (
							<Button variant={'contained'} onClick={() => saveData(true)} className={'me-2 h-12 w-36 font-medium'}>
								{t('payment')}
							</Button>
						)}
						<Button className={'h-12 w-36'} onClick={() => saveData(false)}>
							{t('save')}
						</Button>
					</Box>
				)
			)}

			<ManagementModal
				open={open}
				onClose={handleCloseModal}
				title={t('add_file')}
				titleClassName={'text-center grow '}
				subClassName={' flex flex-row-reverse'}
				className={'w-full rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseModal} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={loading} onClick={addFile} variant="contained">
							{t('register')}
						</LoadingButton>
					</>
				}
			>
				<Box className={'w-full'}>
					<TextField className={'mb-6 mt-6'} variant={'outlined'} fullWidth label={t('title_file')} value={label} onChange={handleLabelChange} />
					<Upload className="flex w-full" text={t('upload_file')} setFiles={setFiles} files={files.filter(file => !!file)} multiple />
				</Box>
			</ManagementModal>
			<BasicModal
				open={!!deleteFile}
				onClose={handleCloseDeleteDialog}
				title={t('delete_role')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={loading} onClick={handleDeleteFile} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_file')}</Typography>
			</BasicModal>
			<BasicModal
				open={finishModal}
				onClose={handleCloseFinish}
				title={t('finish_request')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseFinish} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={loading} onClick={handleClickFinished} variant="contained" color="primary">
							{t('finish_request')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('finish_request_close')}</Typography>
			</BasicModal>
		</Box>
	);
};

export default Studio;

export const studioLoader = ({ params }: LoaderFunctionArgs) => {
	const { type } = params;

	if (!types || !types.includes(type + '')) {
		throw new Response('Not Found', { status: 404 });
	}
	return null;
};
